import React from "react";
import { FiCheck, FiInfo, FiRefreshCw, FiShield } from "react-icons/fi";

function VerificationFieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-3 block text-sm font-semibold text-[#1B163B]">
      {children} <span className="text-[#EF4444]">*</span>
    </label>
  );
}

function PayoutAuthenticatorVerification({
  title,
  description,
  info,
  inputIdPrefix,
}: {
  title: string;
  description: string;
  info: string;
  inputIdPrefix: string;
}) {
  const [code, setCode] = React.useState<string[]>(Array(6).fill(""));

  const updateCode = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    setCode((current) => {
      const next = [...current];
      next[index] = digit;
      return next;
    });

    if (digit && index < 5) {
      const nextInput = document.getElementById(
        `${inputIdPrefix}-${index + 1}`,
      );
      nextInput?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === "Backspace" && !code[index] && index > 0) {
      document.getElementById(`${inputIdPrefix}-${index - 1}`)?.focus();
    }
  };

  return (
    <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-6 new-shadow">
      <div className="mb-6 flex items-start gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[8px] bg-[#A9B2CF1A] text-xl text-[#1B163B]">
          <FiShield />
        </span>
        <div>
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            {title}
          </h2>
          <p className="mt-2 text-sm font-medium text-[#505E8E]">
            {description}
          </p>
        </div>
      </div>

      <div>
        <div className="flex min-h-[86px] items-center justify-between rounded-[8px] border border-[#1B163B] bg-white px-5 text-left">
          <span className="flex items-center gap-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-[8px] bg-[#16A34A1A] text-[#16A34A]">
              <FiShield />
            </span>
            <span>
              <span className="block text-sm font-semibold text-[#1B163B]">
                Google Authenticator
              </span>
              <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                Recommended for payout account changes
              </span>
            </span>
          </span>
          <FiCheck className="text-[#16A34A]" />
        </div>
      </div>

      <div className="mt-8">
        <VerificationFieldLabel>Verification Code</VerificationFieldLabel>
        <div className="flex flex-wrap gap-3">
          {code.map((digit, index) => (
            <input
              key={index}
              id={`${inputIdPrefix}-${index}`}
              value={digit}
              inputMode="numeric"
              maxLength={1}
              onChange={(event) => updateCode(index, event.target.value)}
              onKeyDown={(event) => handleKeyDown(index, event)}
              className="h-14 w-14 rounded-[8px] border border-[#E0E5F2] bg-white text-center text-xl font-semibold text-[#1B163B] outline-none focus:border-[#1B163B]"
            />
          ))}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-6">
        <button
          type="button"
          onClick={() => setCode(Array(6).fill(""))}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#1B163B]"
        >
          <FiRefreshCw /> Resend code
        </button>
      </div>

      <div className="mt-4 flex gap-3 rounded-[6px] bg-[#F4F7FE] px-4 py-3 text-sm font-semibold text-[#384B88]">
        <FiInfo className="mt-0.5 shrink-0" />
        <span>{info}</span>
      </div>
    </section>
  );
}

export default PayoutAuthenticatorVerification;
